import React, { useState, useRef, useEffect, useCallback } from "react";
/* [2026-09-08] `htmlDokumentuNaEkran` = sanitizeHtml + zamiana klas edytora na style inline.
   Bez tej zamiany regulamin sformatowany w panelu (wyrównanie, wcięcia, listy punktowane)
   wyświetlałby się pracownikowi płasko — `sanitizeHtml` nie przepuszcza atrybutu `class`. */
import { htmlDokumentuNaEkran } from "../common/EdytorTresci/TrescDokumentu";
import { apiService } from "../../api/apiService";
import { FiFileText, FiCheck, FiUser, FiCalendar } from "react-icons/fi";
import OknoBramki, { TrescHtml } from "../common/OknoBramki/OknoBramki";

/**
 * Bramka regulaminu platformy — konta nie da się używać bez akceptacji.
 *
 * WYGLĄD MA WSPÓLNY z komunikatem (components/common/OknoBramki). Wcześniej oba okna
 * były kopiami tego samego kodu i mimo to WYGLĄDAŁY INACZEJ — regulamin miał zaszytą
 * zieleń #059669/#047857 (sześć miejsc w arkuszu i raz w JS), której nie ma nigdzie
 * w palecie aplikacji, mocniejsze przyciemnienie tła, inne rozmycie i inną szerokość.
 * Tutaj zostaje wyłącznie LOGIKA regulaminu; kanon okna realizuje OknoBramki.
 *
 * BRAMKA NIE MA ✕ ANI onClose — to jest zamierzone. Jedyne wyjście prowadzi przez
 * przycisk akceptacji, odblokowany dopiero po przewinięciu treści do końca.
 */

const formatujDate = (dateStr) => {
    if (!dateStr) return "";
    return new Date(dateStr).toLocaleString("pl-PL", {
        day: "2-digit",
        month: "long",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    });
};

/* `onZamknijWPodgladzie` — [polecenie właściciela 2026-09-20] ✕ WYŁĄCZNIE w trybie „Zaloguj jako”
   (Layout podaje je tylko przy aktywnym podglądzie); zamknięcie lokalne, nic nie akceptuje. */
const TermsOfServiceModal = ({ terms, onAccepted, onZamknijWPodgladzie = null }) => {
    const [canAccept, setCanAccept] = useState(false);
    const [scrollProgress, setScrollProgress] = useState(0);
    const [isAccepting, setIsAccepting] = useState(false);
    const contentRef = useRef(null);

    useEffect(() => {
        if (!terms) return;
        setCanAccept(false);
        setScrollProgress(0);
    }, [terms]);

    // Treść krótsza niż okno nie da się przewinąć — wtedy akceptacja od razu dostępna.
    useEffect(() => {
        if (!contentRef.current || !terms) return;
        const el = contentRef.current;
        if (el.scrollHeight <= el.clientHeight + 5) {
            setCanAccept(true);
            setScrollProgress(100);
        }
    }, [terms]);

    const handleScroll = useCallback((e) => {
        const { scrollTop, scrollHeight, clientHeight } = e.target;
        const maxScroll = scrollHeight - clientHeight;
        if (maxScroll <= 0) {
            setScrollProgress(100);
            setCanAccept(true);
            return;
        }
        const progress = Math.min(100, (scrollTop / maxScroll) * 100);
        setScrollProgress(progress);
        if (scrollTop + clientHeight >= scrollHeight - 5) {
            setCanAccept(true);
        }
    }, []);

    const handleAccept = async () => {
        if (!canAccept || isAccepting) return;
        setIsAccepting(true);
        try {
            await apiService.acceptTerms(terms.id);
            onAccepted();
        } catch (error) {
            console.error("Błąd akceptacji regulaminu:", error);
        } finally {
            setIsAccepting(false);
        }
    };

    if (!terms) return null;

    return (
        <OknoBramki
            ton="regulamin"
            ikona={FiFileText}
            odznaka={`Regulamin platformy v${terms.version}`}
            tytul={terms.title}
            meta={[
                { ikona: FiUser, tekst: terms.authorName },
                { ikona: FiCalendar, tekst: formatujDate(terms.publishedAt) },
            ]}
            postep={scrollProgress}
            onScrollTresci={handleScroll}
            refTresci={contentRef}
            podpowiedz={
                canAccept ? null : "Przewiń do końca, aby zaakceptować regulamin"
            }
            stopkaInfo={{
                ikona: FiFileText,
                tekst: "Wymagana akceptacja regulaminu platformy",
            }}
            akcja={{
                etykieta: "Zapoznałem się i akceptuję regulamin",
                ikona: FiCheck,
                onClick: handleAccept,
                zablokowana: !canAccept,
                ladowanie: isAccepting,
            }}
            /* Regulamin stoi WARSTWĘ WYŻEJ niż komunikat — decyzja przeniesiona
               z poprzedniego arkusza („z-index NAD komunikatami"). */
            poziom="nad"
            onZamknijWPodgladzie={onZamknijWPodgladzie}
        >
            {/* BRIEF-0017: sanityzacja z allowlistą (utils/sanitizeHtml).
                [A16-07] Regulamin pisze ADMINISTRATOR — nie ma tu nadawcy zewnętrznego,
                więc nie ma kogo śledzić pikselem. Zostawiamy zdalne obrazy włączone,
                żeby logotypy w regulaminie wyglądały dokładnie tak jak dotąd. */}
            <TrescHtml
                html={htmlDokumentuNaEkran(terms.content)}
            />
        </OknoBramki>
    );
};

export default TermsOfServiceModal;
